import { Checkbox, Table } from "@mantine/core";
import { useState } from "react";
import type { Props as BaseTableProps } from "./BaseTable";
import BaseTable from "./BaseTable";

export interface SelectableTableProps extends BaseTableProps {
  onSelectionChange?: (selectedIndices: number[]) => void;
  selectedIndices?: number[];
  disabledSelection?: boolean;
}

const SelectableTable = ({
  headers,
  rows,
  loading = false,
  onSelectionChange,
  selectedIndices: controlledSelectedIndices,
  disabledSelection = false,
  ...props
}: SelectableTableProps) => {
  const isControlled = typeof controlledSelectedIndices !== "undefined";
  const [internalSelected, setInternalSelected] = useState<number[]>([]);
  const selectedIndices = isControlled
    ? controlledSelectedIndices
    : internalSelected;

  const allSelected = selectedIndices.length === rows.length && rows.length > 0;
  const partiallySelected = selectedIndices.length > 0 && !allSelected;

  const updateSelection = (newSelected: number[]) => {
    if (!isControlled) {
      setInternalSelected(newSelected);
    }
    onSelectionChange?.(newSelected);
  };

  const toggleAll = () => {
    if (disabledSelection) return;
    const newSelected = allSelected ? [] : rows.map((_, idx) => idx);
    updateSelection(newSelected);
  };

  const toggleOne = (index: number) => {
    if (disabledSelection) return;
    const newSelected = selectedIndices.includes(index)
      ? selectedIndices.filter((i) => i !== index)
      : [...selectedIndices, index];
    updateSelection(newSelected);
  };

  const newHeaders = disabledSelection
    ? headers
    : [
        <Checkbox
          id="select-checkbox"
          key="select-all"
          checked={allSelected}
          indeterminate={partiallySelected}
          onChange={toggleAll}
        />,
        ...headers,
      ];

  const newRows = rows.map((row, index) => {
    const isSelected = selectedIndices.includes(index);

    return (
      <Table.Tr
        key={row.key ?? `row-${index}`}
        className={isSelected ? "bg-blue-50" : undefined}
      >
        {!disabledSelection && (
          <Table.Td className="w-10">
            <Checkbox checked={isSelected} onChange={() => toggleOne(index)} />
          </Table.Td>
        )}
        {row.props.children}
      </Table.Tr>
    );
  });

  return (
    <BaseTable
      headers={newHeaders}
      rows={newRows}
      loading={loading}
      allowSorting={props.allowSorting}
      {...props}
    />
  );
};

export default SelectableTable;
